import React, { useEffect, useState } from "react";
import millify from "millify";
import { FaDiscord } from "./icons/sharedIcons";
import { useT } from "@jammable/ui-core/src/i18n";

export const DISCORD_URL = "https://discord.gg/jammable";

/**
 * The Discord invite, styled consistently wherever it appears.
 *
 * Five variants because the link does different jobs:
 *
 *   banner the prominent one: white F card, blurple button, live counts
 *   pill   a standalone call to action, Discord blurple, for a page that has
 *          room for one (the end of a cover page, an empty state)
 *   card   the same but full-width with a line of supporting copy, for dead
 *          ends like 404/500 where it is the main thing left to do
 *   soft   a pale blurple tile for design-language-F pages (voice page)
 *   inline a text link that sits inside a sentence, for places that already
 *          have a primary action and must not compete with it
 *
 * Blurple (#5865F2) is Discord's own brand colour rather than Jammable's, which
 * is deliberate: people recognise it instantly, and it should read as "that is
 * the Discord button", not as another site button.
 */
const BLURPLE = "#5865F2";

// One fetch per page load no matter how many CTAs are mounted. The endpoint is
// CDN-cached, and a failure just means the banner renders without numbers.
let statsPromise = null;
function useDiscordStats(enabled) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    if (!enabled) return;
    if (!statsPromise) {
      statsPromise = fetch("/api/discord/stats")
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    }
    let live = true;
    statsPromise.then((d) => live && d?.members && setStats(d));
    return () => {
      live = false;
    };
  }, [enabled]);
  return stats;
}
const BLURPLE_HOVER = "#4752c4";

export default function DiscordCta({
  variant = "pill",
  label,
  className = "",
  children,
}) {
  const stats = useDiscordStats(variant === "banner");
  const t = useT();

  const common = {
    href: DISCORD_URL,
    target: "_blank",
    rel: "noopener noreferrer",
  };

  if (variant === "inline") {
    return (
      <a
        {...common}
        className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-font-bold tw-underline tw-underline-offset-2 tw-transition-opacity hover:tw-opacity-80 tw-cursor-pointer ${className}`}
        style={{ color: BLURPLE }}
      >
        <FaDiscord className="tw-size-4 tw-flex-shrink-0" />
        {children || label || "Discord"}
      </a>
    );
  }

  if (variant === "banner") {
    // The prominent one, in design language F: a white card with a hairline
    // border (holds up on white and lavender pages alike), Jammable ink for
    // the copy, and blurple only on the icon and the button so it still reads
    // as Discord. It used to be a blurple gradient with a hover shadow. Live
    // member/online counts - 11k members is a better argument for joining
    // than any copy we could write. Numbers are optional: if the stats call
    // fails the row simply is not rendered.
    return (
      <a
        {...common}
        className={`tw-group tw-flex tw-w-full tw-flex-col sm:tw-flex-row sm:tw-items-center tw-gap-4 tw-rounded-[28px] tw-border-2 tw-border-solid tw-border-jam-line tw-bg-white tw-p-5 sm:tw-p-6 tw-no-underline tw-cursor-pointer tw-transition-colors hover:tw-border-jam-soft ${className}`}
      >
        <span
          className="tw-flex tw-size-14 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full"
          style={{ backgroundColor: BLURPLE }}
        >
          <FaDiscord className="tw-size-8 tw-text-white" />
        </span>

        <span className="tw-flex tw-flex-col tw-text-left tw-gap-1">
          <span className="jam-display tw-text-lg sm:tw-text-xl tw-font-extrabold tw-text-jam-ink tw-leading-tight">
            {label || t("nav.discordAria")}
          </span>
          <span className="tw-text-sm tw-font-semibold tw-text-jam-muted">
            {children || t("home.discord.bannerSub")}
          </span>
          {stats && (
            <span className="tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-[13px] tw-font-bold tw-text-jam-ink">
              <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-jam-lav tw-px-3 tw-py-1">
                <span className="tw-relative tw-flex tw-size-2">
                  <span className="tw-absolute tw-inline-flex tw-h-full tw-w-full tw-animate-ping tw-rounded-full tw-bg-emerald-400 tw-opacity-75" />
                  <span className="tw-relative tw-inline-flex tw-size-2 tw-rounded-full tw-bg-emerald-500" />
                </span>
                {t("home.discord.online", { n: millify(stats.online) })}
              </span>
              <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-jam-lav tw-px-3 tw-py-1">
                <span className="tw-size-2 tw-rounded-full tw-bg-jam-soft" />
                {t("home.discord.members", { n: millify(stats.members) })}
              </span>
            </span>
          )}
        </span>

        <span
          className="sm:tw-ml-auto tw-inline-flex tw-flex-shrink-0 tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-px-5 tw-py-3 tw-text-sm tw-font-extrabold tw-text-white tw-transition-colors"
          style={{ backgroundColor: BLURPLE }}
        >
          <FaDiscord className="tw-size-[18px]" />
          {t("home.discord.joinDiscord")}
        </span>
      </a>
    );
  }

  if (variant === "soft") {
    // Design-language-F pages (the voice page sidebar): a pale blurple tile,
    // no gradient or shadow, so it sits quietly next to the page's own cards.
    return (
      <a
        {...common}
        className={`tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-[24px] tw-bg-[#EEF0FF] tw-p-4 tw-no-underline tw-cursor-pointer ${className}`}
      >
        <span
          className="tw-flex tw-size-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full"
          style={{ backgroundColor: BLURPLE }}
        >
          <FaDiscord className="tw-size-5 tw-text-white" />
        </span>
        <span className="tw-flex tw-min-w-0 tw-flex-grow tw-flex-col tw-text-left">
          <span className="tw-text-sm tw-font-extrabold tw-text-jam-ink">
            {label || t("nav.discordAria")}
          </span>
          <span className="tw-text-xs tw-font-semibold tw-text-jam-muted">
            {children || t("home.discord.softSub")}
          </span>
        </span>
        <span
          className="tw-flex-shrink-0 tw-text-[13px] tw-font-extrabold"
          style={{ color: BLURPLE }}
        >
          {t("home.discord.join")}
        </span>
      </a>
    );
  }

  if (variant === "card") {
    // Surface is white with a hairline border, matching the site's own cards
    // (Reuse Voices / Retry with another voice). A translucent blurple wash was
    // the first attempt and it picked up whatever was behind it - on the pink
    // gradient of /contactus it read pink, not Discord. The colour lives on the
    // icon tile and the title instead, where it is unambiguous on any page.
    return (
      <a
        {...common}
        className={`tw-group tw-flex tw-items-center tw-gap-3.5 tw-w-full tw-rounded-[26px] tw-bg-white tw-p-4 tw-transition-colors tw-cursor-pointer tw-no-underline tw-border-2 tw-border-solid tw-border-jam-line hover:tw-border-jam-soft ${className}`}
      >
        <span
          className="tw-flex tw-size-11 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full"
          style={{ backgroundColor: BLURPLE }}
        >
          <FaDiscord className="tw-size-6 tw-text-white" />
        </span>
        <span className="tw-flex tw-flex-col tw-text-left tw-gap-0.5">
          <span className="tw-text-[15px] tw-font-extrabold tw-text-jam-ink">
            {label || t("nav.discordAria")}
          </span>
          <span className="tw-text-[13px] tw-font-semibold tw-text-jam-muted">
            {children || t("home.discord.cardSub")}
          </span>
        </span>
        <span
          className="tw-ml-auto tw-hidden sm:tw-inline-flex tw-flex-shrink-0 tw-items-center tw-rounded-full tw-px-3.5 tw-py-2 tw-text-[13px] tw-font-bold tw-text-white tw-transition-transform group-hover:tw-scale-105"
          style={{ backgroundColor: BLURPLE }}
        >
          {t("home.discord.join")}
        </span>
      </a>
    );
  }

  // pill
  return (
    <a
      {...common}
      onMouseEnter={(e) =>
        (e.currentTarget.style.backgroundColor = BLURPLE_HOVER)
      }
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BLURPLE)}
      className={`tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-px-4 tw-py-2.5 tw-text-sm tw-font-bold tw-text-white tw-no-underline tw-transition-all tw-cursor-pointer active:tw-scale-[0.98] ${className}`}
      style={{ backgroundColor: BLURPLE }}
    >
      <FaDiscord className="tw-size-[18px]" />
      {label || children || t("nav.discordJoin")}
    </a>
  );
}
